import type { ISolution, SOLUTION_TYPE } from '@eureka/solution-card/src/types';

export interface IAnalysisDirectory {
    key?: string;
    name: string;
    source_type: 'model' | 'customize';
}

export interface IReportCardInstance {
    getBase64Str: () => Promise<Record<string, string>>;
    refreshCard: () => Promise<void>;
}

export enum EReportTaskStatus {
    PROCESSING = 'PROCESSING',
    FINISH = 'FINISH',
    FAILED = 'FAILED'
}

export interface ChartData {
    dimensions: string[];
    source: Record<string, unknown>[];
    extra?: Record<string, unknown>;
}

export interface IRoadmapChartData {
    categories: string;
    start_year: number;
    end_year: number;
    value: string;
    patent_ids: string[];
}

export interface IReportCardCompany {
    id: string;
    name: string;
    logo: string;
    summary: string;
    type: string;
    strength_weakness?: string;
}

export interface IChatItem {
    point: string;
    description: string;
    patents?: string[];
    strength_weakness?: string;
}

export interface ICompanyItem {
    entity_id: string,
    display_name: string,
}
export interface IProject {
    project_id: string;
    project_title: string;
    icon: TIconType;
    data_updated_at: string;
    created_at: string;
    pinned?: boolean;
    patent_count: number;
    paper_count: number;
    tag: string[];
    patent_type: string[];
    country: string[];
    company_list: ICompanyItem[],
    default_company_list: ICompanyItem[],
    project_version?: string
}

export interface IProjectListResponse {
    project_list: IProject[];
    allow_create: boolean;
}

export type TSize = 'sm' | 'md' | 'lg'

export type TIconType = 1 | 2 | 3 | 4 | 5 | 6

export interface IProjectInfo {
    id?: string,
    title?: string,
    icon?: TIconType,
    lastUpdate?: string,
    company_list?: ICompanyItem[],
    default_company_list?: ICompanyItem[],
    project_version?: string
}

export type TChartType = 'apd_y' // （图一：专利趋势）
    | 'paper_datetime_ts' // （图二：文献趋势）
    | 'apd_y_country' // （图三：专利趋势）
    | 'paper_country_name_standard' // （图四：文献过去5年总量）

export interface IRightListComponentData {
    project_id?: string;
    solution_ids?: string[];
    chart_type?: TChartType;
    solution_type?: SOLUTION_TYPE;
    category: string,
    vector_query: string,
    dimension_list?: {
        field: string;
        value: string;
    }[]
}

export interface IRightListRequest extends IRightListComponentData {
    page: number,
    limit: number,
}

export interface ISolutionWithInnovation extends ISolution {
    innovation_point: string[],
    /**
     * 技术问题
     */
    tech_question: string
}

export interface ISuggestList {
    PATENT_ID: string;
    PN: string;
}

export interface IExportParams {
    project_id: string,
    background_patent_trends: string,
    background_paper_trends: string,
    technical_status_patent: string,
    technical_status_paper: string,
    technology_roadmap: string,
}

export interface ISaveCompanyParams {
    project_id: string,
    companies: ICompanyItem[]
}

export interface GenerateLoadingProcess {
    step: string,
    content: string,
    haveFinish: boolean
}

export interface IHistoryResponse {
    business_type: string,
    business_id: string,
    modules: Array<{
        module_type: string,
        records: Array<{
            session_id: string,
            input: string,
            output: string,
            create_at: string,
        }>
    }>
}

export enum FlagTimeLineCategory {
    MILESTONE = 'milestone',
    SCHEDULE = 'schedule'
}

export interface FlagTimeLineItem {
    categories: FlagTimeLineCategory,
    node_list: {
        title: string;
        desc?: string;
        year: number;
    }[]
}

/**
 *  模块类型
 */
export enum REPORT_TEMPLATE {
    /**
     * 技术背景目标 (2 chart)
     */
    TECHNICAL_BACKGROUND = 'TECHNICAL_BACKGROUND',
    /**
   * 技术现状分析(2 chart)
   */
    TECHNOLOGY_STATUS_CHALLENGES = 'TECHNOLOGY_STATUS_CHALLENGES',
    /**
     * 具体研究内容
     */
    MARKET_ANALYSIS = 'MARKET_ANALYSIS',
    /**
     * 路线图
     */
    TECHNOLOGICAL_EVOLUTION_PATH = 'TECHNOLOGICAL_EVOLUTION_PATH',
    /**
     * 主要玩家
     */
    HEAD_PLAYER_ANALYSIS = 'HEAD_PLAYER_ANALYSIS',
    /**
     * 技术方案梳理
     */
    TECHNICAL_SCHEME_REVIEW = 'TECHNICAL_SCHEME_REVIEW',
    /**
     * 重点专利
     */
    KEY_PATENT_INTERPRETATION = 'KEY_PATENT_INTERPRETATION',
    /**
     * 灵感总结
     */
    IDEA_SUMMARY = 'IDEA_SUMMARY',
    /**
     * AI自定义
     */
    CUSTOM_MODULE = 'CUSTOM_MODULE'
}

export interface CustomCardInfo {
    CUSTOM_GPT: string;
}

export interface IdeaSummaryCardInfo {
    IDEA_SUMMARY_GPT: IChatItem[];
}

export interface CoreInnovationCardInfo {
    KEY_PATENT_INTERPRETATION_GPT: ISolutionWithInnovation[];
}

export interface CoreInnovationCardData {
    solutionList?: ISolutionWithInnovation[];
    isAddBusy?: boolean;
}

export interface TechBgCardData {
    description: string
    patentChartData?: ChartData
    paperChartData?: ChartData
}

export interface HeadPlayerCardData {
    description: string;
    companyList?: IReportCardCompany[];
}

export interface MarketAnalysisCardData {
    description: string;
}

export interface ITechSolutionUI extends IChatItem {
    loading: boolean,
    isExpand: boolean,
    detail?: IChatItem[],
}

export type TechSchemeCardData = ITechSolutionUI[]

export interface TechRoadmapCardData {
    roadmapData: IRoadmapChartData[];
    flagData: FlagTimeLineItem[];
    years: number[]
}

export interface TechChallengeCardData {
    description: string;
    patentChartData?: ChartData;
    paperChartData?: ChartData;
}

/**
 * 接口返回数据结构
 */
export interface IReportCardInfo {
    [REPORT_TEMPLATE.TECHNICAL_BACKGROUND]?:{
        TECHNICAL_BACKGROUND_GPT: string;
        TECHNICAL_BACKGROUND_PATENT_CHART: ChartData;
        TECHNICAL_BACKGROUND_PAPER_CHART: ChartData;
    };
    [REPORT_TEMPLATE.TECHNOLOGY_STATUS_CHALLENGES]?: {
        TECHNOLOGY_STATUS_GPT: string[];
        TECHNOLOGY_STATUS_PATENT_CHART: ChartData;
        TECHNOLOGY_STATUS_PAPER_CHART: ChartData;
    };
    [REPORT_TEMPLATE.MARKET_ANALYSIS]?: {
        OBJECTIVES_GPT: string[];
    }
    [REPORT_TEMPLATE.TECHNOLOGICAL_EVOLUTION_PATH]?: {
        TECHNOLOGICAL_EVOLUTION_PATH_GPT: {
            chart: {
                CHART: IRoadmapChartData[];
                SUMMARY: string;
                START: number;
                END: number;
            },
            flag_chart: FlagTimeLineItem[]
        }
    };
    [REPORT_TEMPLATE.HEAD_PLAYER_ANALYSIS]?: {
        HEAD_PLAYER_GPT?: {
            comprehensive_overview: string;
            key_player_analysis: IReportCardCompany[];
        },
    };
    [REPORT_TEMPLATE.TECHNICAL_SCHEME_REVIEW]?: {
        SOLUTION_GPT_FIRST: IChatItem[];
    }
    [REPORT_TEMPLATE.KEY_PATENT_INTERPRETATION]?: CoreInnovationCardInfo
    [REPORT_TEMPLATE.IDEA_SUMMARY]?: IdeaSummaryCardInfo
    [REPORT_TEMPLATE.CUSTOM_MODULE]?: CustomCardInfo
    task_status: EReportTaskStatus;
}

export interface CatalogItem {
    name: string;
    task_group_type: string;
    module_type: REPORT_TEMPLATE;
    /**
     * 是否完成请求
     */
    finished: boolean;
}

export interface CatalogsInViewItem extends CatalogItem {
    el: Element | null
}

export interface TrackData {
    source_type?: string;
    other_param?: string;
    event_detail_str?: string;
    module_type?: string;
    event_type?: string;
}
